import React, { useState, useEffect } from 'react';
import { useTrance } from './TranceContext';
import { motion } from 'framer-motion';
import { Menu, X, Zap, Music, Compass } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ModeSelectionModal from './ModeSelectionModal';

const Navbar = () => {
  const { isTranceMode, toggleTranceMode } = useTrance();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModeModalOpen, setIsModeModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Releases', href: '/releases' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleNavigation = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      if (location.pathname === path) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate(href);
      }
    } else {
      navigate(href);
      window.scrollTo(0, 0);
    }
  };

  const handlePartyClick = () => {
    if (!isTranceMode) {
      // If turning ON, toggle mode AND navigate to /party
      toggleTranceMode();
      navigate('/party');
    } else {
      // If turning OFF, just toggle mode
      toggleTranceMode();
      if (location.pathname === '/party') {
        navigate('/');
      }
    }
  };

  const handleTranceClick = () => {
    navigate('/trance');
  };

  const handleCosmicClick = () => {
    navigate('/cosmic');
  };

  const handleShroomsClick = () => {
    navigate('/shrooms');
  };

  const handleLSDClick = () => {
    navigate('/lsd');
  };

  const handleCrusaderClick = () => {
    navigate('/crusader');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="text-2xl font-heading font-bold tracking-wider text-gradient-platinum flex items-center gap-2 group">
          <span className={`transition-all duration-300 ${isTranceMode ? "text-primary animate-pulse text-glow" : "group-hover:text-primary group-hover:text-glow"}`}>KOROV</span>
          <span className="text-xs text-muted-foreground tracking-[0.3em] hidden sm:block group-hover:text-foreground transition-colors">DHARMAR</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => handleNavigation(e, link.href)}
              className="text-sm font-medium text-foreground/80 hover:text-primary hover:text-glow transition-all duration-300 uppercase tracking-widest relative group cursor-pointer"
            >
              {link.name}
              <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-primary transition-all duration-300 group-hover:w-full shadow-[0_0_10px_hsl(var(--primary))]" />
            </a>
          ))}
          
          <Button
            onClick={() => setIsModeModalOpen(true)}
            variant="outline"
            className="ml-4 border-primary/50 animate-border bg-gradient-to-r from-primary/20 via-[#ff8800]/20 to-[#8B4513]/20 text-white hover:from-primary/30 hover:via-[#ff8800]/30 hover:to-[#8B4513]/30 hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] hover:border-primary transition-all duration-500 font-bold tracking-wider"
          >
            <Compass className="w-4 h-4 mr-2" />
            CHOOSE YOUR PATH
          </Button>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex items-center gap-2">
          <Button
            onClick={() => setIsModeModalOpen(true)}
            size="sm"
            variant="outline"
            className="transition-all duration-500 border-white/30 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 text-white hover:from-cyan-500/30 hover:via-purple-500/30 hover:to-pink-500/30"
          >
            <Compass className="w-3 h-3" />
          </Button>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-foreground hover:text-primary transition-colors p-2"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-white/10 p-4 flex flex-col gap-4"
        >
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => handleNavigation(e, link.href)}
              className="text-lg font-heading text-foreground hover:text-primary hover:text-glow text-center py-2 transition-all duration-300 cursor-pointer"
            >
              {link.name}
            </a>
          ))}
        </motion.div>
      )}

      {/* Mode Selection Modal */}
      <ModeSelectionModal isOpen={isModeModalOpen} onClose={() => setIsModeModalOpen(false)} />
    </nav>
  );
};

export default Navbar;
